import type { BaseRecord, CreateResponse, HttpError } from "@refinedev/core";
import { useApiUrl, useCustom, useCustomMutation } from "@refinedev/core";
import type {
  IUkhoNoticeCounts,
  IUkhoNoticeEmail,
  IUkhoNoticeRow,
  IUkhoUnmatchedRow,
  IUkhoWorkRow,
  IVesselNoticeView,
  IVesselTransfer,
  IVesselTransferRequest,
} from "interfaces";
import { useCallback } from "react";

/**
 * Data hooks of the UKHO transfer notices (emr-api /ukho-transfer-notice).
 *
 * Read hooks return { data, isLoading, isFetching, isError, error, refetch }
 * for the CURRENT request only. <Refine> defaults every query to
 * placeholderData: keepPreviousData, which would hand the previous vessel,
 * notice or search result to the next request with isLoading false; each read
 * hook switches that off. `data` is undefined until this request's response
 * arrives (Refine's own `result.data` is a frozen `{}` while loading).
 * `isLoading` is true while this request has no data yet, `isFetching` during
 * any fetch (a refetch included). `refetch` keeps its identity and is a no-op
 * while the hook is disabled (useNoticeEmail with a null id). A read does not
 * retry: a failure reaches `isError` at once (the app's default would spin
 * for about 7 s first).
 *
 * Mutation hooks return the members of Refine's useCustomMutation
 * ({ mutate, mutateAsync, mutation }), so `mutation.isPending` works as in
 * the other pages. mutate / mutateAsync take the call's own variables (the
 * notice id goes into the URL, so the hook cannot fix it) and optional
 * { onSuccess, onError, onSettled } callbacks. onSuccess gets the response
 * ({ data }); mutateAsync rejects with the raw error.
 *
 * No hook raises Refine's generic toast: the screen shows its own texts. A
 * failure arrives as the raw AxiosError (this app's axios instance has no
 * response interceptor, so `statusCode` is never set and `message` is axios's
 * own sentence): read the status from `response.status` and the text through
 * apiErrorMessage().
 *
 * The API answers only DistributorSuperAdmin, DistributorAdmin and
 * DistributorManagement (403 otherwise): mount the consumers behind
 * useCan({ resource: ResourceEnum.UkhoTransferNotice, action: "list" }).
 */

/** The error a failed call delivers: the raw AxiosError, typed as it arrives. */
export type IUkhoApiError = HttpError & {
  response?: { status?: number; data?: { message?: string | string[] } };
};

export interface IUkhoReadResult<TData> {
  data: TData | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: IUkhoApiError | null;
  refetch: () => Promise<unknown>;
}

export interface IUkhoMutateOptions<TData> {
  onSuccess?: (response: CreateResponse<TData>) => void;
  onError?: (error: IUkhoApiError) => void;
  onSettled?: () => void;
}

/** A park on the UKHO leaving day. The API would default a missing day to today, so it is required here. */
export interface IParkWithDateRequest extends IVesselTransferRequest {
  transfer_type: "to_old_vessels";
  effective_date: string;
  reason: string;
}

/** A reactivation on the UKHO arriving day. The day is required, as for a park. */
export interface IReactivateWithDateRequest extends IVesselTransferRequest {
  transfer_type: "from_old_vessels";
  effective_date: string;
  reason: string;
}

type QueryParams = Record<string, string | boolean | undefined>;

interface IPostRequest {
  url: string;
  values: object;
}

const messageText = (message: unknown): string => {
  if (Array.isArray(message)) {
    return message.join(", ");
  }
  return typeof message === "string" ? message : "";
};

/** What to show for a failed call: the API's sentence (an array joined), else the error's own message. */
export const apiErrorMessage = (
  error: IUkhoApiError | null | undefined,
): string =>
  messageText(error?.response?.data?.message) ||
  messageText(error?.message) ||
  "Request failed";

const noticeUrl = (apiUrl: string, path: string): string =>
  `${apiUrl}/ukho-transfer-notice/${path}`;

/** The search term, left out of the query when blank (the API trims it too). */
const searchParams = (q: string): QueryParams => {
  const searchText = q.trim();
  return searchText ? { q: searchText } : {};
};

const workListParams = (handled: boolean, q: string): QueryParams => ({
  handled,
  ...searchParams(q),
});

const resolveNothing = (): Promise<unknown> => Promise.resolve(undefined);

const useNoticeRead = <TData extends BaseRecord>(
  path: string,
  query: QueryParams = {},
  enabled = true,
): IUkhoReadResult<TData> => {
  const apiUrl = useApiUrl();
  const hasQuery = Object.keys(query).length > 0;
  const { query: response } = useCustom<TData, IUkhoApiError>({
    url: noticeUrl(apiUrl, path),
    method: "get",
    config: hasQuery ? { query } : undefined,
    // placeholderData: undefined overrides <Refine>'s keepPreviousData default,
    // retry: false its 3 retries (about 7 s before isError).
    queryOptions: { enabled, placeholderData: undefined, retry: false },
    errorNotification: false,
  });
  return {
    data: response.data?.data,
    isLoading: response.isLoading,
    isFetching: response.isFetching,
    isError: response.isError,
    error: response.error,
    // A disabled query still fetches when refetch() is called.
    refetch: enabled ? response.refetch : resolveNothing,
  };
};

const useCustomPost = <TVariables, TData extends BaseRecord>(
  buildPostRequest: (apiUrl: string, variables: TVariables) => IPostRequest,
) => {
  const apiUrl = useApiUrl();
  const { mutate, mutateAsync, mutation } = useCustomMutation<
    TData,
    IUkhoApiError
  >();
  const buildMutationParams = useCallback(
    (variables: TVariables) => ({
      ...buildPostRequest(apiUrl, variables),
      method: "post" as const,
      // The dialogs show their own confirmation and error texts.
      successNotification: false as const,
      errorNotification: false as const,
    }),
    [apiUrl, buildPostRequest],
  );
  const mutatePost = useCallback(
    (variables: TVariables, options?: IUkhoMutateOptions<TData>) =>
      mutate(buildMutationParams(variables), options),
    [mutate, buildMutationParams],
  );
  const mutatePostAsync = useCallback(
    (variables: TVariables, options?: IUkhoMutateOptions<TData>) =>
      mutateAsync(buildMutationParams(variables), options),
    [mutateAsync, buildMutationParams],
  );
  return { mutate: mutatePost, mutateAsync: mutatePostAsync, mutation };
};

// Reads

/** GET vessel/:vesselId, the "UKHO transfers" block of the vessel page. */
export const useVesselNotices = (vesselId: number) =>
  useNoticeRead<IVesselNoticeView>(`vessel/${vesselId}`);

/** GET work/left-not-parked; `handled` adds this year's settled rows. */
export const useLeftNotParked = (handled: boolean, q: string) =>
  useNoticeRead<IUkhoWorkRow[]>(
    "work/left-not-parked",
    workListParams(handled, q),
  );

/** GET work/back-still-parked; `handled` adds this year's settled rows. */
export const useBackStillParked = (handled: boolean, q: string) =>
  useNoticeRead<IUkhoWorkRow[]>(
    "work/back-still-parked",
    workListParams(handled, q),
  );

/**
 * GET unmatched. There is no `reason` argument on purpose: the API's optional
 * exact-match `reason` is not sent. The tab fetches every unmatched notice and
 * filters by reason group on the client (its four options map onto the five
 * reasons, and the list is tens of rows).
 */
export const useUnmatched = (q: string) =>
  useNoticeRead<IUkhoUnmatchedRow[]>("unmatched", searchParams(q));

/** GET counts, the live counts on the three tabs. */
export const useNoticeCounts = () => useNoticeRead<IUkhoNoticeCounts>("counts");

/** GET :id/email, the stored email. No request while `id` is null. */
export const useNoticeEmail = (id: number | null) =>
  useNoticeRead<IUkhoNoticeEmail>(`${id}/email`, undefined, id !== null);

// Mutations

interface IMatchNoticeVariables {
  id: number;
  vesselId: number;
}

interface IUnmatchNoticeVariables {
  id: number;
}

const buildMatchNoticeRequest = (
  apiUrl: string,
  { id, vesselId }: IMatchNoticeVariables,
): IPostRequest => ({
  url: noticeUrl(apiUrl, `${id}/match`),
  values: { vesselId },
});

const buildUnmatchNoticeRequest = (
  apiUrl: string,
  { id }: IUnmatchNoticeVariables,
): IPostRequest => ({
  url: noticeUrl(apiUrl, `${id}/unmatch`),
  values: {},
});

const buildVesselTransferRequest = (
  apiUrl: string,
  body: IVesselTransferRequest,
): IPostRequest => ({
  url: `${apiUrl}/vessel-transfer`,
  values: body,
});

/** POST :id/match with { vesselId }. Call mutate({ id, vesselId }). */
export const useMatchNotice = () =>
  useCustomPost<IMatchNoticeVariables, IUkhoNoticeRow>(buildMatchNoticeRequest);

/** POST :id/unmatch. Call mutate({ id }). */
export const useUnmatchNotice = () =>
  useCustomPost<IUnmatchNoticeVariables, IUkhoNoticeRow>(
    buildUnmatchNoticeRequest,
  );

/**
 * POST /vessel-transfer, a park on the UKHO leaving day. Call mutate(body)
 * with an IParkWithDateRequest: target_shipco_id is Deactivated Vessels,
 * effective_date is YYYY-MM-DD.
 */
export const useParkWithDate = () =>
  useCustomPost<IParkWithDateRequest, IVesselTransfer>(
    buildVesselTransferRequest,
  );

/**
 * POST /vessel-transfer, a reactivation on the UKHO arriving day. Call
 * mutate(body) with an IReactivateWithDateRequest: target_shipco_id is the
 * shipco the vessel returns to, effective_date is YYYY-MM-DD.
 */
export const useReactivateWithDate = () =>
  useCustomPost<IReactivateWithDateRequest, IVesselTransfer>(
    buildVesselTransferRequest,
  );
